import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { IconAlertTriangle, IconChevronDown, IconLock, IconStar, IconTrash } from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { TeamFlag } from "@/components/team-flag";
import { GateBadge } from "@/components/gate-badge";
import { useEvent } from "@/api/hooks";
import { useFleetEqualization } from "@/api/hooks/use-fleet-equalization";
import { useSetCorrectorSlot, useClearCorrectorState } from "@/api/hooks/use-corrector-state";
import { CheckedCard, FLEET_ROW_INNER } from "@/pages/fleet-measurements/_shared";
import { cn, fmt } from "@/lib/utils";
import { asBuildId, asEventId } from "@/types/identifiers";
import type { BuildId, CorrectorSlot } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type BoatEntry = components["schemas"]["FleetEqualizationBoatEntry"];

// Plan-view ordering: bow at top, transom at bottom; Port left, Stbd right.
// POD is centerline so it sits alone in the middle column at its X position.
type SlotRow = {
  label: string;
  xLabel: string;
  portSlot: CorrectorSlot | null;
  stbdSlot: CorrectorSlot | null;
  centerSlot: CorrectorSlot | null;
};

const SLOT_ROWS: SlotRow[] = [
  { label: "Bow Shelf",   xLabel: "12.0", portSlot: "BOW_SHELF_P",   stbdSlot: "BOW_SHELF_S",   centerSlot: null },
  { label: "DB Rake Ram", xLabel: "9.6",  portSlot: "DB_RAKE_RAM_P", stbdSlot: "DB_RAKE_RAM_S", centerSlot: null },
  { label: "Wetbox",      xLabel: "7.0",  portSlot: "WETBOX_P",      stbdSlot: "WETBOX_S",      centerSlot: null },
  { label: "Pod",         xLabel: "6.0",  portSlot: null,            stbdSlot: null,            centerSlot: "POD" },
  { label: "FC Seat",     xLabel: "4.0",  portSlot: "FC_SEAT_P",     stbdSlot: "FC_SEAT_S",     centerSlot: null },
  { label: "Transom",     xLabel: "1.0",  portSlot: "TRANSOM_P",     stbdSlot: "TRANSOM_S",     centerSlot: null },
];

// F50 plan-view silhouette (same path as the per-team F50 SVGs in
// sailgp-geospatialmap-panel). Outer perimeter + two hull cutouts.
const F50_PATH =
  "M 143.308 7.374 L 155.739 9.032 L 158.222 184.708 L 167.753 204.598 L 224.515 205.841 L 262.636 86.099 L 273.822 85.684 L 307.797 201.697 L 371.604 201.697 L 377.405 181.809 L 381.134 13.589 L 391.908 5.717 L 399.366 184.293 L 404.336 201.283 L 403.507 436.208 L 390.665 479.713 L 152.423 480.541 L 135.849 441.181 L 132.121 207.911 L 139.164 184.293 L 143.308 7.374 Z M 166.925 264.26 L 253.935 265.919 L 251.862 427.921 L 165.267 427.507 L 166.925 264.26 Z M 369.119 262.189 L 372.847 425.437 L 289.151 427.921 L 289.152 263.847 L 369.119 262.189 Z";


function deltaTone(delta: number | null | undefined): string {
  if (delta === null || delta === undefined) return "text-muted-foreground";
  if (Math.abs(delta) < 0.5) return "text-muted-foreground";
  if (delta > 0) return "text-amber-700 dark:text-amber-400";
  return "text-blue-700 dark:text-blue-400";
}

/** Stacked weight-plates glyph — three centred bars. Inherits text color. */
function PlatesIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" className={className} aria-hidden>
      <rect x="3.5" y="3" width="9" height="2.4" rx="1.2" />
      <rect x="2.5" y="6.8" width="11" height="2.4" rx="1.2" />
      <rect x="3" y="10.6" width="10" height="2.4" rx="1.2" />
    </svg>
  );
}

/** −/+ stepper for whole-kg corrector mass. Tap = ±1; press-and-hold
 *  auto-repeats (same ergonomics as the yaw check stepper). Ghost buttons —
 *  the number is the anchor, no chrome until pressed. */
function CorrectorStepper({
  value,
  onStep,
  onSet,
  onCommit,
  disabled,
  hasMass,
}: {
  value: number;
  onStep: (delta: 1 | -1) => void;
  onSet: (v: number) => void;
  onCommit: () => void;
  disabled: boolean;
  hasMass: boolean;
}) {
  const holdRef = useRef<{
    timeout?: ReturnType<typeof setTimeout>;
    interval?: ReturnType<typeof setInterval>;
  }>({});

  const start = (dir: 1 | -1) => (e: React.PointerEvent) => {
    e.preventDefault();
    if (disabled || (dir === -1 && value <= 0)) return;
    onStep(dir);
    holdRef.current.timeout = setTimeout(() => {
      holdRef.current.interval = setInterval(() => onStep(dir), 110);
    }, 400);
    const stop = () => {
      clearTimeout(holdRef.current.timeout);
      clearInterval(holdRef.current.interval);
    };
    window.addEventListener("pointerup", stop, { once: true });
    window.addEventListener("pointercancel", stop, { once: true });
  };

  const btn =
    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-base leading-none text-muted-foreground/70 select-none touch-manipulation transition-colors hover:bg-muted hover:text-foreground active:scale-90 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground/70";

  return (
    <div className="flex items-center justify-center gap-1.5">
      <button
        type="button"
        aria-label="remove 1 kg"
        onPointerDown={start(-1)}
        disabled={disabled || value <= 0}
        className={btn}
      >
        −
      </button>
      <label
        className={cn(
          "flex h-7 min-w-[3rem] cursor-text items-center justify-center gap-1 rounded-md px-1.5 text-sm font-bold tabular-nums leading-none transition-colors focus-within:ring-2 focus-within:ring-ring",
          hasMass ? "bg-teal-600 text-white" : "text-muted-foreground",
        )}
      >
        {hasMass && <PlatesIcon className="h-3.5 w-3.5 shrink-0 opacity-90" />}
        <input
          type="text"
          inputMode="numeric"
          aria-label="corrector mass in kg"
          value={value === 0 ? "" : value}
          disabled={disabled}
          placeholder="0"
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "");
            onSet(digits === "" ? 0 : parseInt(digits, 10));
          }}
          onBlur={onCommit}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          className="w-8 bg-transparent text-center leading-none outline-none placeholder:opacity-50 disabled:cursor-not-allowed"
        />
        {hasMass && <span className="text-[9px] font-semibold opacity-75">kg</span>}
      </label>
      <button
        type="button"
        aria-label="add 1 kg"
        onPointerDown={start(1)}
        disabled={disabled}
        className={btn}
      >
        +
      </button>
    </div>
  );
}

function SlotEditor({
  buildId,
  slot,
  currentMass,
  suggestedMass,
  locked,
}: {
  buildId: BuildId;
  slot: CorrectorSlot;
  currentMass: number;
  suggestedMass: number | undefined;
  locked: boolean;
}) {
  const mutation = useSetCorrectorSlot(buildId);
  const [draft, setDraft] = useState(Math.round(currentMass));
  const draftRef = useRef(draft);

  // Sync to the committed value whenever it changes (our own refetch after a
  // save, or another client). While stepping, currentMass is unchanged so the
  // local draft survives until the debounced commit lands.
  useEffect(() => {
    const v = Math.round(currentMass);
    draftRef.current = v;
    setDraft(v);
  }, [currentMass]);

  // Stepping is local + debounced: tap freely, it saves once when you pause —
  // one mutation, not one per tap / hold-repeat. "apply" commits immediately.
  const commitTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const commit = useCallback(
    (value: number, immediate = false) => {
      clearTimeout(commitTimer.current);
      if (value === Math.round(currentMass)) return;
      const fire = () =>
        mutation.mutate(
          { slot, mass_kg: value },
          {
            onError: (err) => {
              toast.error(err.message);
              const v = Math.round(currentMass);
              draftRef.current = v;
              setDraft(v);
            },
          },
        );
      if (immediate) fire();
      else commitTimer.current = setTimeout(fire, 500);
    },
    // mutation identity churns each render; calling mutate on it is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentMass, slot],
  );

  const step = useCallback(
    (delta: 1 | -1) => {
      const next = Math.max(0, draftRef.current + delta);
      draftRef.current = next;
      setDraft(next);
      commit(next);
    },
    [commit],
  );

  // Direct entry: whole-kg, non-negative. Debounced like stepping; the field's
  // blur flushes it immediately so tabbing away saves without the 500 ms wait.
  const setValue = useCallback(
    (raw: number) => {
      const next = Math.max(0, Math.round(raw) || 0);
      draftRef.current = next;
      setDraft(next);
      commit(next);
    },
    [commit],
  );
  const commitNow = useCallback(() => commit(draftRef.current, true), [commit]);

  const disabled = mutation.isPending || locked;
  const hasMass = draft > 0;
  const suggested = suggestedMass === undefined ? undefined : Math.round(suggestedMass);
  const hasSuggestion = suggested !== undefined && suggested !== draft && !locked;

  return (
    <div className="px-0.5">
      <div className="flex items-center justify-center gap-1.5 h-5 mb-0.5">
        {hasMass && !locked && (
          <button
            type="button"
            onClick={() => {
              draftRef.current = 0;
              setDraft(0);
              commit(0, true);
            }}
            disabled={disabled}
            title="Clear this slot to 0"
            aria-label="clear this slot to 0 kg"
            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-xs leading-none text-muted-foreground/60 transition-colors hover:bg-rose-100 hover:text-rose-600 active:scale-90 disabled:opacity-40 dark:hover:bg-rose-950/50 dark:hover:text-rose-400"
          >
            ×
          </button>
        )}
        {hasSuggestion && (
          <button
            type="button"
            onClick={() => {
              draftRef.current = suggested;
              setDraft(suggested);
              commit(suggested, true);
            }}
            disabled={disabled}
            title={`Apply suggested ${suggested} kg`}
            className="inline-flex items-center gap-0.5 rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-bold leading-none text-orange-700 tabular-nums transition-colors hover:bg-orange-200 active:scale-95 disabled:opacity-50 dark:bg-orange-950/60 dark:text-orange-300 dark:hover:bg-orange-900"
          >
            → {suggested}
          </button>
        )}
      </div>
      <CorrectorStepper
        value={draft}
        onStep={step}
        onSet={setValue}
        onCommit={commitNow}
        disabled={disabled}
        hasMass={hasMass}
      />
    </div>
  );
}

const ON_TARGET_THRESHOLD = 0.5; // kg — anything tighter is "on target"

/** Visual indicator of ΔCWmass magnitude — bar when adjustment is needed,
 *  "on target" pill when correctors already match the reference within 0.5 kg. */
function CwmassMarker({ cwmass }: { cwmass: number | null | undefined }) {
  if (cwmass == null) return null;
  if (Math.abs(cwmass) <= ON_TARGET_THRESHOLD) {
    return (
      <div className="flex items-center justify-center">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider uppercase bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
          </svg>
          on target
        </span>
      </div>
    );
  }
  const w = 140, h = 22;
  const cap = 50;
  const v = Math.min(Math.abs(cwmass), cap);
  const pct = v / cap;
  const tone =
    cwmass < 0
      ? "fill-emerald-500"
      : cwmass < 5
      ? "fill-blue-400"
      : cwmass < 15
      ? "fill-amber-500"
      : "fill-red-500";
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block w-full">
      <rect x={6} y={9} width={w - 12} height={4} rx={2} className="fill-muted" />
      <rect x={6} y={9} width={Math.max(2, (w - 12) * pct)} height={4} rx={2} className={tone} />
    </svg>
  );
}

function BoatRowExpanded({ boat }: { boat: BoatEntry }) {
  const buildId = asBuildId(boat.event_build_id);
  const locked = boat.event_build_signed_off;
  const clearMutation = useClearCorrectorState(buildId);

  const currentMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of boat.current_correctors) m.set(c.slot, c.mass_kg);
    return m;
  }, [boat.current_correctors]);

  const suggestedMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of boat.suggested_allocation) m.set(c.slot, c.mass_kg);
    return m;
  }, [boat.suggested_allocation]);

  return (
    <div className="border-t border-border/50 px-4 py-3">
      {/* Per-boat headline metrics (moved out of the row header for parity with db-height) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-xs">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Current correctors</div>
          <div className="text-sm font-semibold tabular-nums">{fmt(boat.current_corrector_total, 0)} kg</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Target CWmass</div>
          <div className="text-sm font-semibold tabular-nums">{fmt(boat.cwmass, 0)} kg</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Δ CWmass</div>
          <div className={`text-sm font-semibold tabular-nums ${deltaTone(boat.delta_cwmass)}`}>
            {boat.delta_cwmass == null
              ? "—"
              : `${boat.delta_cwmass > 0 ? "+" : ""}${fmt(boat.delta_cwmass, 0)} kg`}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">VCG</div>
          <div className="text-sm font-semibold tabular-nums">{fmt(boat.vcg, 3)} m</div>
        </div>
      </div>

      {/* Feasibility / slack */}
      {boat.allocation_feasible === false && (
        <div className="mb-3 rounded-md border border-red-500 bg-red-50 dark:bg-red-950/30 px-3 py-2 flex items-center gap-2 text-xs">
          <IconAlertTriangle className="w-4 h-4 text-red-700 dark:text-red-400 shrink-0" />
          <div>
            <div className="font-semibold text-red-900 dark:text-red-100">
              LP infeasible — partial allocation shown
            </div>
            {boat.allocation_slack && (
              <div className="text-red-700 dark:text-red-300 mt-0.5 tabular-nums">
                Would need: LCG slack {fmt(boat.allocation_slack.lcg, 3)} m
                {boat.allocation_slack.tcg !== undefined &&
                  ` · TCG slack ${fmt(boat.allocation_slack.tcg, 3)} m`}
              </div>
            )}
          </div>
        </div>
      )}

      {locked && (
        <div className="mb-3 rounded-md border border-border bg-muted/30 px-3 py-2 flex items-center gap-2 text-xs text-muted-foreground">
          <IconLock className="w-4 h-4 shrink-0" />
          <span>
            This build is signed off — corrector state is read-only. Unlock the build on the
            build detail page to edit.
          </span>
        </div>
      )}

      {!locked && boat.allocation_feasible && boat.allocation_total_mass_moved != null && (
        <div className="mb-2 text-[11px] text-muted-foreground tabular-nums">
          LP suggested allocation — best LCG/TCG match, fewest slots ({fmt(boat.allocation_total_mass_moved, 0)} kg to move).
          Click "apply" to copy a suggested value into the slot.
        </div>
      )}

      {!locked && boat.current_corrector_total > 0 && (
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={() => {
              if (
                window.confirm(
                  `Clear all corrector weight for ${boat.boat_code}? This zeroes every slot.`,
                )
              ) {
                clearMutation.mutate(undefined, {
                  onSuccess: () => toast.success("Corrector weight cleared"),
                  onError: (err) => toast.error(err.message),
                });
              }
            }}
            disabled={clearMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-input px-2.5 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:border-rose-400 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
          >
            <IconTrash className="h-3.5 w-3.5" />
            {clearMutation.isPending ? "Clearing…" : "Clear all weight"}
          </button>
        </div>
      )}

      {/* Plan-view slot grid: Port left, Stbd right, bow at top, transom at
          bottom. POD is centerline. Faded F50 silhouette sits behind so the
          inputs literally overlay the boat layout. */}
      <div className="relative mx-auto w-full max-w-2xl">
        <div className="text-center text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground/70 mb-1">
          Bow · X = 12 m
        </div>
        <div className="relative">
          <svg
            viewBox="121.818 -0.625 292.597 500.625"
            preserveAspectRatio="none"
            className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.08] dark:opacity-[0.12]"
            aria-hidden
          >
            <path d={F50_PATH} className="fill-foreground" />
          </svg>
          <div
            className="relative grid gap-y-2 py-2"
            style={{ gridTemplateColumns: "1fr 110px 1fr", columnGap: "16px" }}
          >
            {SLOT_ROWS.map((row) => (
              <Fragment key={row.label}>
                {row.portSlot ? (
                  <SlotEditor
                    buildId={buildId}
                    slot={row.portSlot}
                    currentMass={currentMap.get(row.portSlot) ?? 0}
                    suggestedMass={suggestedMap.get(row.portSlot)}
                    locked={locked}
                  />
                ) : (
                  <div />
                )}

                {row.centerSlot ? (
                  <div className="self-center">
                    <div className="text-center text-[10px] font-bold uppercase tracking-wider text-muted-foreground leading-tight mb-1">
                      {row.label}
                      <span className="ml-1 text-[9px] font-normal text-muted-foreground/60 tabular-nums normal-case tracking-normal">
                        X={row.xLabel}
                      </span>
                    </div>
                    <SlotEditor
                      buildId={buildId}
                      slot={row.centerSlot}
                      currentMass={currentMap.get(row.centerSlot) ?? 0}
                      suggestedMass={suggestedMap.get(row.centerSlot)}
                      locked={locked}
                    />
                  </div>
                ) : (
                  <div className="self-center text-center px-1">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground leading-tight">
                      {row.label}
                    </div>
                    <div className="text-[9px] text-muted-foreground/60 tabular-nums">
                      X = {row.xLabel}
                    </div>
                  </div>
                )}

                {row.stbdSlot ? (
                  <SlotEditor
                    buildId={buildId}
                    slot={row.stbdSlot}
                    currentMass={currentMap.get(row.stbdSlot) ?? 0}
                    suggestedMass={suggestedMap.get(row.stbdSlot)}
                    locked={locked}
                  />
                ) : (
                  <div />
                )}
              </Fragment>
            ))}
          </div>
        </div>
        <div className="text-center text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground/70 mt-1">
          Transom · X = 1 m
        </div>
        <div className="flex items-center justify-between mt-1 text-[9px] font-bold tracking-widest text-muted-foreground/60">
          <span>PORT · +Y</span>
          <span>STBD · −Y</span>
        </div>
      </div>
    </div>
  );
}

function RowMetrics({ boat }: { boat: BoatEntry }) {
  // Bare = weighing-time anchor (stable). Corrected = bare + installed
  // correctors (changes as operator adjusts EventCorrectorState).
  // Both come from the backend; mutations invalidate this query so the
  // refetch picks up fresh values automatically.
  const hasCorrectors = boat.current_corrector_total > 0;

  return (
    <div className="inline-grid grid-cols-[auto_auto_auto_auto] gap-x-2 gap-y-0.5 items-baseline text-[11px] tabular-nums">
      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">W</span>
      <span className="font-semibold text-foreground text-right">
        {fmt(boat.weight, 1)}
        <span className="ml-1 text-[10px] text-muted-foreground font-normal">bare</span>
      </span>
      <span
        className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
          hasCorrectors
            ? "bg-blue-500/10 text-blue-700 dark:text-blue-300"
            : "text-muted-foreground/30"
        }`}
      >
        {hasCorrectors ? "+ corr" : "—"}
      </span>
      <span className={hasCorrectors ? "font-semibold text-foreground" : "text-muted-foreground/30"}>
        {fmt(boat.current_weight, 1)}
      </span>

      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">LCG</span>
      <span className="text-foreground text-right">{fmt(boat.lcg, 3)}</span>
      <span
        className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
          hasCorrectors
            ? "bg-blue-500/10 text-blue-700 dark:text-blue-300"
            : "text-muted-foreground/30"
        }`}
      >
        {hasCorrectors ? "+ corr" : "—"}
      </span>
      <span className={hasCorrectors ? "text-foreground" : "text-muted-foreground/30"}>
        {fmt(boat.current_lcg, 3)}
      </span>

      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">TCG</span>
      <span className="text-foreground text-right">{fmt(boat.tcg, 3)}</span>
      <span
        className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
          hasCorrectors
            ? "bg-blue-500/10 text-blue-700 dark:text-blue-300"
            : "text-muted-foreground/30"
        }`}
      >
        {hasCorrectors ? "+ corr" : "—"}
      </span>
      <span className={hasCorrectors ? "text-foreground" : "text-muted-foreground/30"}>
        {fmt(boat.current_tcg, 3)}
      </span>
    </div>
  );
}

function BoatRow({
  boat,
  expanded,
  onToggle,
  onOpenWeighings,
}: {
  boat: BoatEntry;
  expanded: boolean;
  onToggle: () => void;
  onOpenWeighings: () => void;
}) {
  const borderClass = !boat.has_data
    ? "border-l-border"
    : boat.is_reference
    ? "border-l-amber-500"
    : "border-l-blue-400";

  // Headline Δ-to-target — on the identity line on mobile, in its own column on
  // desktop, so it never overlaps the metric grid on a narrow screen.
  const deltaEl = boat.has_data ? (
    <span className={`text-xs font-semibold tabular-nums ${deltaTone(boat.delta_cwmass)}`}>
      {boat.is_reference
        ? "ref"
        : boat.delta_cwmass == null
        ? "—"
        : Math.abs(boat.delta_cwmass) <= ON_TARGET_THRESHOLD
        ? <span className="text-emerald-700 dark:text-emerald-400">on target</span>
        : `${boat.delta_cwmass > 0 ? "+" : ""}${fmt(boat.delta_cwmass, 1)} kg`}
    </span>
  ) : null;
  const chevron = (
    <IconChevronDown
      className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`}
    />
  );

  return (
    <div className={`border-l-3 rounded-r-lg bg-card ${borderClass}`}>
      <div
        className={`${FLEET_ROW_INNER} ${expanded ? "shadow-[0_1px_4px_rgba(0,0,0,0.04)]" : ""}`}
        onClick={() => onToggle()}
      >
        {/* Team chip — flag + code (matches db-height etc.) */}
        <div className="flex items-center gap-3 sm:w-28 sm:shrink-0">
          <TeamFlag iso_alpha2={boat.team_iso_alpha2} size="xs" />
          <span className="font-medium text-[13px]">{boat.team_code}</span>
          {boat.is_reference && (
            <IconStar className="w-3 h-3 text-amber-500 fill-amber-500 shrink-0" aria-label="reference boat" />
          )}
          {boat.event_build_signed_off && (
            <IconLock className="w-3 h-3 text-muted-foreground shrink-0" aria-label="build signed off" />
          )}
          {/* Mobile only: Δ + chevron on the identity line */}
          <div className="flex items-center gap-2 ml-auto sm:hidden">
            {deltaEl}
            {chevron}
          </div>
        </div>

        {/* Data: bare + as-sits (W, LCG, TCG) */}
        <div className="min-w-0 sm:flex-1 sm:px-3">
          <div className="flex flex-wrap items-center gap-2">
            {boat.has_data ? (
              <RowMetrics boat={boat} />
            ) : (
              <span className="text-xs text-muted-foreground italic">
                Missing {boat.missing.join(" and ")} weighing
              </span>
            )}
            {boat.platform_gate_ok === false && <GateBadge />}
          </div>
        </div>

        {/* Visual ΔCWmass indicator (parity with sparkline column on other pages) */}
        <div className="w-[180px] shrink-0 px-2 hidden md:block">
          {boat.has_data && !boat.is_reference && <CwmassMarker cwmass={boat.delta_cwmass} />}
        </div>

        {/* Right column: Δ value + chevron (desktop) */}
        <div className="hidden sm:flex items-center gap-2 w-24 shrink-0 justify-end">
          {deltaEl}
          {chevron}
        </div>
      </div>

      {expanded && boat.has_data && <BoatRowExpanded boat={boat} />}

      {expanded && !boat.has_data && (
        <div className="border-t border-border/50 px-4 py-3">
          <p className="text-sm text-muted-foreground mb-2">
            Cannot compute equalization for this boat until both a platform weighing and an
            APW wing weighing exist on or before the event date.
          </p>
          <Button size="sm" variant="outline" onClick={onOpenWeighings}>
            Open boat weighings
          </Button>
        </div>
      )}
    </div>
  );
}

function SpreadCard({
  label,
  value,
  target,
  ok,
}: {
  label: string;
  value: string;
  target: string;
  ok: boolean;
}) {
  const borderColor = ok ? "border-l-emerald-500" : "border-l-amber-500";
  return (
    <div className={`bg-card border-l-3 ${borderColor} rounded-r-lg pl-4 pr-5 py-3`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
        Fleet {label} spread
      </div>
      <div className="flex items-baseline justify-between gap-3 mt-0.5">
        <div className="text-lg font-bold tabular-nums whitespace-nowrap">{value}</div>
        <div className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">{target}</div>
      </div>
    </div>
  );
}

export function FleetEqualizationPage() {
  const params = useParams();
  const eventId = asEventId(params.eventId ?? "");
  const navigate = useNavigate();

  const { data: event } = useEvent(eventId);
  const { data, isLoading } = useFleetEqualization(eventId);
  const [expandedBoat, setExpandedBoat] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Fleet Equalization" subtitle={event?.name} />
        <div className="mt-6 grid grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
        <div className="mt-6 space-y-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const presentBoats = data.boats.filter((b) => b.has_data);
  const missingBoats = data.boats.filter((b) => !b.has_data);
  const spread = data.spread;

  const weightOk = spread.weight_pct != null && spread.weight_pct <= spread.weight_target_pct;
  const lcgOk = spread.lcg_m != null && spread.lcg_m <= spread.lcg_target_m;
  const tcgOk = spread.tcg_m != null && spread.tcg_m <= spread.tcg_target_m;

  // Reference boat is sorted first (amber border + star); then present boats; then missing.
  const sorted = [
    ...presentBoats.filter((b) => b.is_reference),
    ...presentBoats.filter((b) => !b.is_reference),
    ...missingBoats,
  ];

  return (
    <div>
      <PageHeader
        title="Fleet Equalization"
        subtitle={event?.name}
        count={presentBoats.length}
        countLabel="boats with data"
      />
      <p className="text-[11px] text-muted-foreground mt-1">
        Per-boat target corrector mass to match the heaviest bare-boat weight in the fleet,
        with LP-suggested placement across the 11 slots.
      </p>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <CheckedCard checked={presentBoats.length} total={data.boats.length} />
        <SpreadCard
          label="LCG"
          value={spread.lcg_m == null ? "—" : `${spread.lcg_m.toFixed(3)} m`}
          target={`≤ ${spread.lcg_target_m} m`}
          ok={lcgOk}
        />
        <SpreadCard
          label="TCG"
          value={spread.tcg_m == null ? "—" : `${spread.tcg_m.toFixed(3)} m`}
          target={`≤ ${spread.tcg_target_m} m`}
          ok={tcgOk}
        />
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        <SpreadCard
          label="Weight"
          value={spread.weight_pct == null ? "—" : `${spread.weight_pct.toFixed(2)}%`}
          target={`≤ ${spread.weight_target_pct}%`}
          ok={weightOk}
        />
        {data.reference_boat_code && (
          <div className="bg-card border-l-3 border-l-amber-500 rounded-r-lg pl-4 pr-5 py-3 md:col-span-2 flex items-center gap-3">
            <IconStar className="w-4 h-4 text-amber-500 fill-amber-500 shrink-0" />
            <div className="flex-1">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Reference boat (heaviest)
              </div>
              <div className="text-sm font-semibold">{data.reference_boat_code}</div>
            </div>
            {data.target_lcg != null && (
              <div className="text-right text-[11px] text-muted-foreground tabular-nums">
                <div>Target LCG <span className="font-medium text-foreground">{data.target_lcg.toFixed(3)} m</span></div>
                <div>Target TCG <span className="font-medium text-foreground">{data.target_tcg.toFixed(3)} m</span></div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-2">
        {sorted.map((boat) => (
          <BoatRow
            key={boat.boat_id}
            boat={boat}
            expanded={expandedBoat === boat.boat_id}
            onToggle={() =>
              setExpandedBoat(expandedBoat === boat.boat_id ? null : boat.boat_id)
            }
            onOpenWeighings={() => navigate(`/boats/${boat.boat_id}/weighings`)}
          />
        ))}
      </div>
    </div>
  );
}
